import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { SharedBadge } from '../components/network/Badges';
import ConfirmShareDialog from '../components/network/ConfirmShareDialog';
import ConsoleForOthersToggle from '../components/network/ConsoleForOthersToggle';
import ExtensionDownload from '../components/network/ExtensionDownload';
import ServerAddress from '../components/network/ServerAddress';
import AccessTokens from '../components/network/AccessTokens';
import Panel from '../components/network/Panel';
import { InstallGuideLink } from '../components/network/InstallGuide';
import type { PluginV2Device } from '../types';

function fmtDate(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

/** The connected extension (a live connection): connected devices + a token to pair one.
 *
 * Admins can SHARE one of their own browsers with all users: it then fetches for anyone with no
 * connected browser of their own (theirs always go first). Sharing asks first, because
 * their fetches run with that browser's cookies, sessions and IP — see ConfirmShareDialog.
 *
 * Tokens are shared with the queued extension; both cards show the same AccessTokens
 * section. Devices are this extension's own — "online" means an
 * open socket right now (Centrifugo presence), not a recent heartbeat.
 */
/** `showTokens` off where the page shows the token section once for both cards. */
export default function ConnectedExtensionSettings({ showTokens = true }: { showTokens?: boolean } = {}) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [devices, setDevices] = useState<PluginV2Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDevices(await api.getPluginV2Devices());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the connected extension devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Sharing goes through ConfirmShareDialog (the server refuses it without confirm_shared);
  // un-sharing is immediate — taking a browser back is never the risky direction.
  const [sharing, setSharing] = useState<PluginV2Device | null>(null);
  const [busy, setBusy] = useState(false);

  const setShared = async (d: PluginV2Device, shared: boolean) => {
    setError('');
    setBusy(true);
    try {
      const saved = await api.setPluginV2DeviceSystem(d.id, shared, shared);
      setDevices((all) => all.map((x) => (x.id === saved.id ? saved : x)));
      setSharing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update device');
    } finally {
      setBusy(false);
    }
  };

  const setConsoleForOthers = async (d: PluginV2Device, on: boolean) => {
    setError('');
    setBusy(true);
    try {
      const saved = await api.setPluginV2DeviceConsoleForOthers(d.id, on);
      setDevices((all) => all.map((x) => (x.id === saved.id ? saved : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Who is connected first: it is what this tab is opened to check. */}
      <Panel
        title="Devices"
        hint="Green while its live connection is open."
        actions={
          <button onClick={fetchDevices} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">Refresh</button>
        }
      >
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading…</div>
          ) : devices.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No device has connected yet. <InstallGuideLink build="chrome-connected">How to install</InstallGuideLink>
            </div>
          ) : (
            devices.map((d) => (
              <div key={d.id} className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${d.online ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {d.name || d.device_id}
                  {d.is_system && <SharedBadge since={d.shared_since} />}
                  {isAdmin && (
                    <button
                      onClick={() => (d.is_system ? setShared(d, false) : setSharing(d))}
                      className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {d.is_system ? 'Stop sharing' : 'Share with all users…'}
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {d.online ? 'Connected' : 'Not connected'} · Last connected {fmtDate(d.last_connected_at)} · Max tabs {d.max_tabs}
                </div>
                {isAdmin && d.is_system && (
                  <ConsoleForOthersToggle
                    on={!!d.console_for_others}
                    busy={busy}
                    onChange={(on) => setConsoleForOthers(d, on)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </Panel>

      {showTokens && <AccessTokens />}

      <Panel
        title="Get the connected extension"
        hint="Green dot on its icon. Fetches straight away while it's connected. Chrome only."
        actions={<InstallGuideLink build="chrome-connected">How to install</InstallGuideLink>}
      >
        <div className="p-4 space-y-4">
          <ExtensionDownload kind="connected" />
          <ServerAddress />
        </div>
      </Panel>

      <ConfirmShareDialog
        isOpen={sharing !== null}
        deviceName={sharing?.name || sharing?.device_id || 'this browser'}
        busy={busy}
        onCancel={() => setSharing(null)}
        onConfirm={() => sharing && setShared(sharing, true)}
      />
    </div>
  );
}
